import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin, Store, Car, Shield, Sparkles, Droplets, Star } from "lucide-react";
import Seo from "../components/Seo";
import Marquee from "../components/Marquee";
import {
  Reveal, MaskedLines, Eyebrow, SectionHead, CallBookButtons, PhotoFrame, CtaBand, EASE,
} from "../components/Shared";
import { BUSINESS, HERO_PHOTO, PACKAGES, SERVICES, photo, fmt } from "../data/site";

const serviceIcons = [Sparkles, Car, Shield, Droplets, Star, Store];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <div className="absolute inset-0 hero-bg" />
        <div className="absolute inset-0 hero-grid opacity-60" />
        <div className="absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-bright/20 blur-[140px]" />
        <div className="absolute bottom-[-20%] left-[-10%] h-[480px] w-[480px] rounded-full bg-glow/10 blur-[160px]" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-ink/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
      <div className="absolute inset-0 grain" />

      <motion.div style={{ opacity: fade }} className="relative flex-1 flex items-center mx-auto w-full max-w-7xl px-5 sm:px-8 pt-28 pb-16">
        <div className="max-w-3xl">
          <Reveal y={16}>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-bright" />
              <Eyebrow>Sioux Falls · South Dakota</Eyebrow>
            </div>
          </Reveal>
          <MaskedLines
            lines={["LUXURY AUTO", "DETAILING,", "DONE RIGHT."]}
            delay={0.25}
            className="font-display text-5xl sm:text-7xl lg:text-8xl font-extrabold uppercase tracking-tight leading-[0.95]"
          />
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-7 max-w-xl text-base sm:text-lg text-steel leading-relaxed"
          >
            Interior details, full packages, 2-step paint correction and certified
            System X ceramic coatings — in-shop or at your driveway.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
            className="mt-7 flex flex-wrap gap-2.5"
          >
            {["Flat-rate packages", "Mobile & in-shop", "Free quotes", "Certified installers"].map((c, i) => (
              <span key={i} data-testid={`hero-badge-${i}`} className="flex items-center gap-2 glass border border-white/10 rounded-full px-4 py-2 text-xs text-steel">
                <span className="h-1.5 w-1.5 rounded-full bg-bright" /> {c}
              </span>
            ))}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-9"
          >
            <CallBookButtons testPrefix="hero" />
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
        className="relative border-t border-white/10 glass"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { k: "Detailing packages", v: "From $275" },
            { k: "Paint correction", v: "2-Step · From $400" },
            { k: "Ceramic coatings", v: "System X Certified" },
            { k: "Service", v: "Mobile & In-Shop" },
          ].map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted">{s.k}</p>
              <p className="mt-1 text-sm sm:text-base font-semibold text-white">{s.v}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}

function ServicesBento() {
  const spans = ["", "", "", "", "", "", "", ""];
  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
        <SectionHead
          eyebrow="01 · What we do"
          title="Every surface, every detail."
          lead="Eight ways we bring your vehicle back to showroom condition — each with mobile or in-shop service."
        />
        <Reveal delay={0.15}>
          <Link to="/pricing" data-testid="home-services-all-link" className="hidden lg:inline-flex items-center gap-1.5 text-sm text-steel hover:text-bright transition-colors border border-white/10 rounded-full px-5 py-2.5">
            View all pricing <ArrowUpRight size={14} />
          </Link>
        </Reveal>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((s, i) => {
          const Icon = serviceIcons[i % serviceIcons.length];
          return (
            <Reveal key={s.path} delay={i * 0.06} className={spans[i]}>
              <Link
                to={s.path}
                data-testid={`home-service-card-${s.path.replace(/\//g, "")}`}
                className="card-sheen group relative flex h-full flex-col justify-between rounded-2xl border border-white/10 bg-card p-7 transition-all duration-300 hover:bg-cardhover hover:border-bright/40 hover:-translate-y-1 min-h-[210px]"
              >
                <div className="flex items-start justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-bright/10 border border-bright/20 text-bright">
                    <Icon size={20} />
                  </span>
                  <span className="font-mono text-[11px] text-muted group-hover:text-bright transition-colors">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className="mt-8">
                  <h3 className="font-display text-xl font-bold text-white">{s.name}</h3>
                  <p className="mt-1.5 text-sm text-muted">{s.desc}</p>
                  <p className="mt-3 font-mono text-xs text-bright">{s.price}</p>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function Packages() {
  return (
    <section className="bg-coal border-y border-white/5 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead
          eyebrow="02 · Packages"
          title="Three ways to book."
          lead="Flat-rate packages — pricing varies by your vehicle's size, never its condition. Mobile service at your location adds a little more."
        />
        <div className="mt-14 grid gap-5 lg:grid-cols-3">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1}>
              <div
                data-testid={`home-package-card-${i}`}
                className={`relative h-full rounded-2xl border p-8 transition-all duration-300 hover:-translate-y-1 ${
                  p.featured
                    ? "border-bright/50 bg-gradient-to-b from-bright/10 to-card blue-glow"
                    : "border-white/10 bg-card hover:border-white/20"
                }`}
              >
                {p.featured && (
                  <span className="absolute -top-3 left-8 bg-bright text-white text-[11px] font-mono uppercase tracking-widest rounded-full px-3 py-1">
                    Flagship
                  </span>
                )}
                <h3 className="font-display text-2xl font-bold">{p.name}</h3>
                <div className="mt-5 flex items-end gap-2">
                  <span className="font-display text-5xl font-extrabold">{fmt(p.price)}</span>
                  <span className="text-muted text-sm mb-1.5">starting</span>
                </div>
                <p className="mt-2 text-xs font-mono text-steel">Mobile from {fmt(p.mobilePrice)}</p>
                <p className="mt-4 text-sm text-steel leading-relaxed">{p.blurb}</p>
                <ul className="mt-6 space-y-2">
                  {p.includes.slice(0, 4).map((inc, j) => (
                    <li key={j} className="flex gap-2.5 text-sm text-steel">
                      <span className="mt-2 h-1 w-1 rounded-full bg-bright shrink-0" />
                      {inc}
                    </li>
                  ))}
                  {p.includes.length > 4 && (
                    <li className="text-xs text-muted pt-1">+ {p.includes.length - 4} more included services</li>
                  )}
                </ul>
                <Link
                  to="/book"
                  data-testid={`home-package-book-${i}`}
                  className={`mt-8 flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors ${
                    p.featured ? "bg-bright hover:bg-accent text-white" : "border border-white/20 hover:border-bright hover:text-bright text-white"
                  }`}
                >
                  Book {p.name} <ArrowUpRight size={15} />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-8 text-center">
          <Link to="/pricing" data-testid="home-pricing-link" className="text-sm text-bright hover:text-white transition-colors inline-flex items-center gap-1.5">
            Compare all packages, upgrades & coatings <ArrowUpRight size={14} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function LogoBand() {
  return (
    <section data-testid="home-logo-band" className="relative overflow-hidden py-16 sm:py-24">
      <div className="absolute inset-0 hero-bg" />
      <div className="relative mx-auto max-w-4xl px-5 sm:px-8 text-center">
        <Reveal>
          <img
            src="/aplus-logo.jpg"
            alt="A+ Detailing & Tint logo"
            className="mx-auto w-auto h-36 sm:h-52 lg:h-64"
            loading="lazy"
            data-testid="home-large-logo"
          />
          <p className="mt-6 font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-glow">
            Sioux Falls · South Dakota
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Process() {
  const steps = [
    { n: "01", t: "Assessment", d: "We walk your vehicle with you and recommend the right package — no upsells you don't need." },
    { n: "02", t: "Decontamination", d: "Hand wash, bug and tar removal, wheel cleaning and clay bar treatment where the package calls for it." },
    { n: "03", t: "Correction & Protection", d: "Machine polishing removes swirls and scratches, then paint, leather and trim get their protective finish." },
    { n: "04", t: "Final Inspection", d: "Streak-free glass, conditioned surfaces and a walk-around before you drive away." },
  ];
  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div>
          <SectionHead
            eyebrow="03 · The process"
            title="Method over shortcuts."
            lead="Every package follows the same disciplined sequence — the difference is how deep each stage goes."
          />
          <div className="mt-12 space-y-0">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="flex gap-6 py-6 border-b border-white/5 group" data-testid={`process-step-${i}`}>
                  <span className="font-mono text-sm text-bright pt-1">{s.n}</span>
                  <div>
                    <h3 className="font-display text-xl font-bold group-hover:text-bright transition-colors">{s.t}</h3>
                    <p className="mt-2 text-sm text-steel leading-relaxed">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="grid gap-4">
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-bright/15 via-card to-card p-10 lg:p-12 min-h-[240px] flex flex-col justify-between" data-testid="home-flagship-panel">
              <Eyebrow>Flagship package</Eyebrow>
              <div>
                <p className="font-display text-4xl lg:text-5xl font-extrabold">The Exclusive</p>
                <p className="mt-2 text-sm text-steel">Clay bar, decontamination & Rupes Advanced Nano protectant — 8 months of protection.</p>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 gap-4">
            <Reveal delay={0.18}>
              <div data-testid="home-stat-ceramic" className="rounded-2xl border border-white/10 bg-card p-8 min-h-[200px] flex flex-col justify-between">
                <p className="font-mono text-[11px] uppercase tracking-widest text-bright">System X Max</p>
                <div>
                  <p className="font-display text-4xl font-extrabold">10 yr</p>
                  <p className="mt-1 text-xs text-muted">protection · lifetime warranty</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.26}>
              <div data-testid="home-stat-packages" className="rounded-2xl border border-white/10 bg-card p-8 min-h-[200px] flex flex-col justify-between">
                <p className="font-mono text-[11px] uppercase tracking-widest text-bright">Packages</p>
                <div>
                  <p className="font-display text-4xl font-extrabold">From $275</p>
                  <p className="mt-1 text-xs text-muted">in-shop · mobile available</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function Options() {
  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 sm:pb-32">
      <SectionHead
        eyebrow="04 · Mobile or in-shop"
        title="We come to you — or you come to us."
        lead="Same packages, same discipline, wherever your vehicle is."
      />
      <div className="mt-14 grid gap-5 md:grid-cols-2">
        {[
          {
            icon: Store,
            t: "In-Shop Detailing",
            d: "Drop your vehicle at our Sioux Falls studio and let the full setup go to work.",
            a: BUSINESS.address,
            pts: ["Full studio setup & lighting", "Ceramic coating applications", "Easy drop-off & pickup"],
          },
          {
            icon: Car,
            t: "Mobile Detailing",
            d: "We come to your home or office anywhere in the Sioux Falls area — same packages, your driveway.",
            a: "Mobile service available",
            pts: ["We bring water & power", "Same flat-rate packages", "Zero interruption to your day"],
          },
        ].map((o, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="card-sheen h-full rounded-2xl border border-white/10 bg-card p-9 hover:border-bright/40 transition-colors" data-testid={`home-option-${i}`}>
              <div className="flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-bright/10 border border-bright/20 text-bright">
                  <o.icon size={22} />
                </span>
                <span className="font-mono text-[11px] text-muted">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="mt-6 font-display text-2xl font-bold">{o.t}</h3>
              <p className="mt-3 text-sm text-steel leading-relaxed">{o.d}</p>
              <ul className="mt-6 space-y-2.5">
                {o.pts.map((p) => (
                  <li key={p} className="flex items-center gap-2.5 text-sm text-steel">
                    <span className="h-1.5 w-1.5 rounded-full bg-bright shrink-0" /> {p}
                  </li>
                ))}
              </ul>
              <p className="mt-6 flex items-center gap-2 text-xs font-mono text-muted">
                <MapPin size={13} className="text-bright" /> {o.a}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="A+ Detailing | Auto Detailing & Ceramic Coating in Sioux Falls, SD"
        description="Luxury auto detailing in Sioux Falls, SD. Interior & full detailing, 2-step paint correction, System X ceramic coatings, PPF & window tint. Mobile & in-shop. Call 605-799-5882."
        path="/"
      />
      <Hero />
      <Marquee items={["Interior Detailing", "Full Auto Detailing", "The Exclusive", "2-Step Paint Correction", "System X Ceramic", "Paint Protection Film", "Window Tinting", "Sioux Falls, SD"]} />
      <ServicesBento />
      <Packages />
      <LogoBand />
      <Process />
      <Options />
      <CtaBand testPrefix="home" />
    </>
  );
}
